import { z } from 'zod'
import { api } from '@/lib/api/client'
import { OrigemDoEventoSchema } from '@/types/OrigemDoEvento'

/**
 * `GET /users/{user_id}/events` — uma linha da trilha do usuário
 * (`app/routes/users/events_response.py::UserEventItem`, campos comuns em
 * `app/routes/shared/event_item.py::EventItemBase`).
 *
 * `field`, `kind` e `source` são `str` CRU no wire, **de propósito**: o back
 * documenta que valor novo não pode derrubar o read de um front desatualizado.
 * Tipar qualquer um deles como `z.enum` aqui trocaria uma linha estranha por uma
 * PÁGINA vazia — exatamente o oposto do contrato. Quem renderiza usa mapa com
 * fallback, nunca `switch` exaustivo.
 */
export const UserEventSchema = z
  .object({
    db_id: z.string(),
    /** O único campo próprio do item de user. **Não há `phone_id`/`reason`** —
     *  esses são do item de contato. */
    user_id: z.string(),
    /** `created | deleted | active | name | role | permissions |
     *  permissions_excluded | scopes | scopes_excluded | channel_ids | sub_roles |
     *  password_reset | password_change` — sem garantia no wire. */
    field: z.string(),
    /** Bool vem `"true"`/`"false"`; coleção vem JSON ordenado. **`null` ≠ `''` ≠
     *  `'[]'`**, e em `backfill_log` o `null` significa DESCONHECIDO. */
    from_value: z.string().nullable().default(null),
    /** **ESTADO COMPLETO depois, nunca o delta.** */
    to_value: z.string().nullable().default(null),
    by_user_id: z.string(),
    /** SNAPSHOT do username na época. `"System"` em fluxo interno;
     *  `apikey:<nome>` em ator M2M; em `backfill_inferred` é o fallback do tenant
     *  (`{slug}.dstorres`) e **não** quem fez. */
    by_username: z.string(),
    /** `lifecycle` (created/deleted/active/password_reset/password_change) · `edit` (name) ·
     *  `grant` (RBAC + channel_ids + sub_roles) — `str` no wire. */
    kind: z.string().default('edit'),
    /** `live | backfill_log | backfill_inferred`. Ausente em doc gravado antes de
     *  o campo existir — daí o default, que espelha o do Pydantic. */
    source: z.string().default('live'),
    /** Epoch em **SEGUNDOS** — `new Date(created_at * 1000)`. */
    created_at: z.number(),
    /** Quem/o quê escreveu a linha (faixa 29). `null` em linha antiga; AUSENTE em
     *  back anterior à faixa — daí `.optional()` além de `.nullable()`. `tipo` é
     *  `z.string()`, nunca `z.enum`. Linha do `seed_owner`:
     *  `{tipo: 'script', id: 'script:seed_owner', nome: 'seed_owner'}`. */
    origem: OrigemDoEventoSchema.nullable().optional(),
  })
  .passthrough()

export type UserEvent = z.infer<typeof UserEventSchema>

/**
 * `PageEnvelope` da casa (`app/routes/shared/pagination.py::PageEnvelope`) com a
 * chave do domínio `events`, nunca `items`.
 *
 * Esta rota **não tem filtro**, então `total_unfiltered === total` sempre — a
 * chave existe no envelope e é lida aqui só para o schema não divergir do wire;
 * nenhuma tela deve usá-la para distinguir "0 de N" de "0 de 0".
 */
export const UserEventsPageSchema = z
  .object({
    events: z.array(UserEventSchema),
    total: z.number(),
    total_unfiltered: z.number(),
    page: z.number(),
    limit: z.number(),
  })
  .passthrough()

export type UserEventsPage = z.infer<typeof UserEventsPageSchema>

export interface UserEventsParams {
  /** ≥ 1. Fora do range → 422 (shape nativo do FastAPI, `detail: []`). */
  page?: number
  /** 1–200. */
  limit?: number
}

/**
 * `GET /users/{id}/events` — exige `users:history:read`; **`users:read` NÃO dá o
 * histórico** (403 `FORBIDDEN`).
 *
 * 404 só quando não há doc de user **E** não há nenhum evento: **user
 * hard-deletado continua com trilha**, então esta chamada não pode ser
 * condicionada ao sucesso de `GET /users/{id}`.
 *
 * Ordem do back (`{created_at: -1, _id: -1}`) é a fonte — eventos do mesmo segundo
 * saem em ordem de inserção e **não se reordena no cliente**.
 */
export async function getUserEvents(
  userId: string,
  { page = 1, limit = 50 }: UserEventsParams = {},
): Promise<UserEventsPage> {
  const { data } = await api.get<unknown>(`/users/${userId}/events`, { params: { page, limit } })
  return UserEventsPageSchema.parse(data)
}
